import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom';
import {
  Box, Checkbox, FormControl, MenuItem, OutlinedInput,
  Select, TextField, Typography, Tabs, Tab, Radio,
  RadioGroup, FormControlLabel, CircularProgress, Button, Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ChipInput from '../../../components/ChipInput/ChipInput';
import CustomAutoComplete from "../../../common/CustomAutoComplete";
import { updateObjectStateValue } from "../../../utils/FunctionHelpers";
import { BiSearch } from "react-icons/bi";

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.8125rem',
  fontWeight: 700,
  letterSpacing: '0.07em',
  color: '#94a3b8',
  textTransform: 'uppercase',
  marginBottom: 4,
  display: 'block',
}));

const SectionDivider = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.1em', color: '#03407b', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(to right, #d6eaf8, transparent)' }} />
  </Box>
);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    fontSize: '0.83rem',
    background: '#fff',
    height: 38,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#7fb3d3' },
    '&.Mui-focused fieldset': { borderColor: '#03407b', borderWidth: 1.5 },
  },
};

const multilineSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    fontSize: '0.83rem',
    background: '#fff',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#7fb3d3' },
    '&.Mui-focused fieldset': { borderColor: '#03407b', borderWidth: 1.5 },
  },
};

const ApplyButton = styled(Button)(() => ({
  borderRadius: 8,
  padding: '10px 0',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  background: 'linear-gradient(135deg, #0556a4, #03407b)',
  color: '#fff',
  boxShadow: '0 2px 10px rgba(99,102,241,0.35)',
  width: '100%',
  marginTop: 8,
  transition: 'all 0.18s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #03407b, #022d68)',
    boxShadow: '0 4px 14px rgba(99,102,241,0.45)',
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(0)' },
  '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
}));

const Field = ({ label, children }) => (
  <Box sx={{ mb: 1.25 }}>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </Box>
);

function TabPanel({ value, index, children }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const MultiCritere = ({
  classes, addParam, parameters, searchKeys, setSearchKeys,
  loading, selectionsData, handleSearch, nomFamille,
  handleNameChecked, searchMode, handleSearchModeChange,
  setMultiOpen, onAgentSearch,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [tab, setTab] = useState(0);
  const [iaTab, setIaTab] = useState(0);
  const getInitialAgentText = () => new URLSearchParams(window.location.search).get("agentText") || "";
  const [agentText, setAgentText] = useState(getInitialAgentText);
  const [agentLoading, setAgentLoading] = useState(false);

  const handleDeleteChip = (index) => {
    const newChips = [...searchKeys.motsCle];
    newChips.splice(index, 1);
    updateObjectStateValue(setSearchKeys, { motsCle: newChips });
  };

  const ia = parameters?.iaForm || {};
  const setIA = (patch) => addParam({ target: { name: "iaForm", value: { ...ia, ...patch } } });
  const setExperienceField = (k, v) => setIA({ experience: { ...(ia.experience || {}), [k]: v } });

  const handleApplySearch = () => {
    const urlParams = { ...parameters };
    Object.keys(urlParams).forEach((key) => {
      if (urlParams[key] === '' || urlParams[key] === null || urlParams[key] === undefined) delete urlParams[key];
    });
    history.push(`/accueil/chercher?${new URLSearchParams(urlParams).toString()}`);
    setMultiOpen(false);
  };

  const handleApplyIaSearch = () => {
    const payload = {
      isAiSearch: true, categorie: parameters.categorie || 'candidat',
      loc: ia.loc || [], exclude: ia.exclude || "",
      skills: ia.skills || [], programming_languages: ia.programming_languages || [],
      role: ia.role || "", experience_min: ia.experience?.min,
      experience_max: ia.experience?.max, industry: ia.industry || [],
    };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === '' || payload[key] === null || payload[key] === undefined ||
        (Array.isArray(payload[key]) && payload[key].length === 0)) delete payload[key];
    });
    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) value.forEach((item) => params.append(`${key}[]`, item));
      else params.append(key, value);
    });
    history.push(`/accueil/chercher?${params.toString()}`);
    setMultiOpen(false);
  };

  const handleApplyAgentSearch = async () => {
    const trimmed = agentText.trim();
    if (!trimmed) return;
    const params = new URLSearchParams();
    params.set("categorie", parameters.categorie || "candidat");
    params.set("isAiSearch", "true");
    params.set("iaMode", "agent");
    params.set("agentText", trimmed);
    history.push(`/accueil/chercher?${params.toString()}`);
    setMultiOpen(false);
    setAgentLoading(true);
    try { await onAgentSearch?.(trimmed); } finally { setAgentLoading(false); }
  };

  const iaEmpty = !(ia.loc?.length) && !ia.exclude && !(ia.skills?.length) &&
    !(ia.programming_languages?.length) && !ia.role && !(ia.industry?.length) &&
    !ia.experience?.min && !ia.experience?.max;

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const isAi = sp.get('isAiSearch') === 'true';
    const iaMode = sp.get('iaMode');
    const agentQ = sp.get('agentText') || "";
    setTab(isAi ? 1 : 0);
    setIaTab(iaMode === "agent" || !!agentQ ? 1 : 0);
    if (agentQ) setAgentText(agentQ);
  }, [location.search]);

  const Spinner = ({ id }) => (
    <Box key={id} display="flex" justifyContent="center" p={1}>
      <CircularProgress size={16} sx={{ color: '#03407b' }} />
    </Box>
  );
  const emptyItem = <MenuItem value="" key="-" sx={{ fontSize: '1rem', color: '#94a3b8' }}>—</MenuItem>;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #03407b 0%, #02357a 100%)', px: 1, flexShrink: 0 }}>
        <Tabs
          value={tab} onChange={(_e, v) => setTab(v)} variant="fullWidth" textColor="inherit"
          TabIndicatorProps={{ style: { background: 'rgba(255,255,255,0.9)', height: 3, borderRadius: '3px 3px 0 0' } }}
          sx={{ minHeight: 44 }}
        >
          <Tab sx={{ minHeight: 44, py: 0 }} label={
            <Typography sx={{ color: tab === 0 ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.05em' }}>
              FILTRER PAR MOT CLÉ
            </Typography>
          } />
          <Tab sx={{ minHeight: 44, py: 0 }} label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography sx={{ color: tab === 1 ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: 700, fontSize: '1rem', letterSpacing: '0.05em' }}>
                FILTRER PAR IA
              </Typography>
              <Box sx={{ background: 'rgba(255,255,255,0.22)', borderRadius: 1, px: 0.7, py: 0.15 }}>
                <Typography sx={{ fontSize: '0.57rem', color: '#fff', fontWeight: 800 }}>AI</Typography>
              </Box>
            </Box>
          } />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 1.5, pb: 2, background: '#f8fafc' }}>

        <TabPanel value={tab} index={0}>

          <Field label="Catégorie">
            <FormControl fullWidth sx={fieldSx}>
              <Select value={parameters.categorie} name="categorie" onChange={(e) => addParam(e)} input={<OutlinedInput name="categorie" />}>
                <MenuItem value="candidat" sx={{ fontSize: '0.83rem' }}>Candidat</MenuItem>
                <MenuItem value="contact" sx={{ fontSize: '0.83rem' }}>Contact</MenuItem>
              </Select>
            </FormControl>
          </Field>

          <Field label="Champ de recherche">
            <FormControl fullWidth sx={fieldSx}>
              <Select value={parameters.motsCleCategorie} name="motsCleCategorie" onChange={(e) => addParam(e)} input={<OutlinedInput name="categorie" />}>
                {[
                  { value: "fonction", label: "Fonction" },
                  { value: "champs", label: "Champs" },
                  { value: "notes", label: "Notes" },
                  { value: "cv", label: "CV" },
                  { value: "tous", label: "Tous" },
                ]
                  .filter((i) => i.value !== "cv" || parameters.categorie === "candidat")
                  .map((i) => <MenuItem value={i.value} key={i.value} sx={{ fontSize: '0.83rem' }}>{i.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Field>

          {parameters.categorie !== "contact" && (
            <Field label="Sélection / Vivier">
              <CustomAutoComplete
                listData={selectionsData.selections}
                value={searchKeys.selection || null}
                handleDataChange={(v) => updateObjectStateValue(setSearchKeys, { selection: v })}
                name="selection" onChange={(e) => addParam(e)}
              />
            </Field>
          )}

          <Field label="Mots clés">
            <ChipInput
              value={searchKeys.motsCle}
              onAdd={(chip) => updateObjectStateValue(setSearchKeys, { motsCle: [...searchKeys.motsCle, chip] })}
              onDelete={(_chip, index) => handleDeleteChip(index)}
              onChange={(chips) => updateObjectStateValue(setSearchKeys, { motsCle: chips })}
              margin="dense" variant="outlined" autoComplete="off" fullWidth
            />
            <RadioGroup row value={searchMode} onChange={handleSearchModeChange} name="searchMode" sx={{ mt: 0.75 }}>
              {[{ value: "or", label: "Un des termes" }, { value: "and", label: "Tous les termes" }, { value: "exact", label: "Phrase exacte" }].map(({ value, label }) => (
                <FormControlLabel key={value} value={value}
                  control={<Radio size="small" sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#03407b' }, p: 0.4 }} />}
                  label={<Typography sx={{ fontSize: '0.8125rem', color: '#475569' }}>{label}</Typography>}
                  sx={{ mr: 1.5 }}
                />
              ))}
            </RadioGroup>
          </Field>

          <SectionDivider label="Localisation" />

          <Field label="Zone">
            <TextField name="localisation" value={parameters?.localisation ?? ""} onChange={(e) => addParam(e)} variant="outlined" autoComplete="off" fullWidth sx={fieldSx} />
          </Field>

          <Field label="Sauf (exclure)">
            <TextField name="localisationSauf" value={parameters?.localisationSauf ?? ""} onChange={(e) => addParam(e)} variant="outlined" autoComplete="off" fullWidth sx={fieldSx} />
          </Field>

          {parameters.categorie !== "contact" && (
            <>
              <SectionDivider label="Équipe" />

              <Field label="Consultant">
                <FormControl fullWidth sx={fieldSx}>
                  <Select value={parameters?.sourceur ?? ""} name="sourceur" onChange={(e) => addParam(e)}>
                    {loading ? [<Spinner key="s1" id="s1" />] : [emptyItem, ...[...selectionsData.consultants].sort((a, b) => a.user.localeCompare(b.user, "fr")).map((u) => (
                      <MenuItem value={u.uuid} key={u.uuid} sx={{ fontSize: '0.83rem' }}>{u.user}</MenuItem>
                    ))]}
                  </Select>
                </FormControl>
              </Field>

              <Field label="Delivery Manager">
                <FormControl fullWidth sx={fieldSx}>
                  <Select value={parameters?.consultant ?? ""} name="consultant" onChange={(e) => addParam(e)}>
                    {loading ? [<Spinner key="s2" id="s2" />] : [emptyItem, ...[...selectionsData.managers].sort((a, b) => a.user.localeCompare(b.user, "fr")).map((u) => (
                      <MenuItem value={u.uuid} key={u.uuid} sx={{ fontSize: '0.83rem' }}>{u.user}</MenuItem>
                    ))]}
                  </Select>
                </FormControl>
              </Field>

              <SectionDivider label="Compétences" />

              <Field label="Compétences métiers">
                <FormControl fullWidth sx={fieldSx}>
                  <Select value={parameters?.competenceMetier ?? ""} name="competenceMetier" onChange={(e) => addParam(e)} input={<OutlinedInput name="statutCandidat" />}>
                    {loading ? [<Spinner key="s3" id="s3" />] : [emptyItem, ...[...selectionsData.competenceMetier].sort((a, b) => a.metier.localeCompare(b.metier, "fr")).map((c) => (
                      <MenuItem value={c.uuid} key={c.uuid} sx={{ fontSize: '0.83rem' }}>{c.metier}</MenuItem>
                    ))]}
                  </Select>
                </FormControl>
              </Field>

              <Field label="Compétences sectorielles">
                <FormControl fullWidth sx={fieldSx}>
                  <Select value={parameters?.competenceSecto ?? ""} name="competenceSecto" onChange={(e) => addParam(e)} input={<OutlinedInput name="statutCandidat" />}>
                    {loading ? [<Spinner key="s4" id="s4" />] : [emptyItem, ...[...selectionsData.competenceSecto].sort((a, b) => a.secteur.localeCompare(b.secteur, "fr")).map((c) => (
                      <MenuItem value={c.uuid} key={c.uuid} sx={{ fontSize: '0.83rem' }}>{c.secteur}</MenuItem>
                    ))]}
                  </Select>
                </FormControl>
              </Field>

              <Field label="Compétences techniques">
                <TextField name="competenceTechnique" value={parameters?.competenceTechnique ?? ""} onChange={(e) => addParam(e)} variant="outlined" autoComplete="off" fullWidth sx={fieldSx} />
              </Field>

              <SectionDivider label="Expérience & autres" />

              <Field label="Société">
                <CustomAutoComplete
                  multiple listData={selectionsData.societes}
                  value={searchKeys.societe || null}
                  handleDataChange={(v) => updateObjectStateValue(setSearchKeys, { societe: v })}
                  name="societe" onChange={(e) => addParam(e)}
                />
              </Field>

              <Box sx={{ mb: 1.25 }}>
                <FieldLabel>Expérience (années)</FieldLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TextField name="start" value={parameters?.start ?? ""} onChange={(e) => addParam(e)} variant="outlined" autoComplete="off" fullWidth type="number" placeholder="Min" sx={fieldSx} />
                  <Typography sx={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>—</Typography>
                  <TextField name="end" value={parameters?.end ?? ""} onChange={(e) => addParam(e)} variant="outlined" autoComplete="off" fullWidth type="number" placeholder="Max" sx={fieldSx} />
                </Box>
              </Box>

              <FormControlLabel
                control={<Checkbox checked={nomFamille} onChange={(e) => handleNameChecked(e)} name="nomFamille" size="small" sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#03407b' }, p: 0.5, mr: 0.5 }} />}
                label={<Typography sx={{ fontSize: '1rem', color: '#475569' }}>Nom de famille uniquement</Typography>}
                sx={{ ml: 0, mb: 1 }}
              />
            </>
          )}

          {parameters.categorie === "contact" && (
            <Field label="Société">
              <CustomAutoComplete
                multiple listData={selectionsData.societes}
                value={searchKeys.societe || null}
                handleDataChange={(v) => updateObjectStateValue(setSearchKeys, { societe: v })}
                name="societe" onChange={(e) => addParam(e)}
              />
            </Field>
          )}

          <ApplyButton startIcon={<BiSearch size={15} />} onClick={handleApplySearch}>
            Appliquer la recherche
          </ApplyButton>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', mb: 2 }}>
            <Tabs value={iaTab} onChange={(_e, v) => setIaTab(v)} variant="fullWidth"
              TabIndicatorProps={{ style: { background: '#03407b', height: 2 } }}
              sx={{ background: '#fff', borderBottom: '1px solid #e2e8f0', minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }, '& .MuiTab-root.Mui-selected': { color: '#03407b' } }}
            >
              <Tab label="Par formulaire" />
              <Tab label="Par texte (Agent)" />
            </Tabs>
          </Box>

          <TabPanel value={iaTab} index={0}>
            <Field label="Localisation">
              <ChipInput value={ia.loc || []} onChange={(chips) => setIA({ ...ia, loc: chips })} placeholder="Ex: Maroc, Rabat…" fullWidth variant="outlined" />
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontStyle: 'italic', mt: 0.5 }}>Choix multiples</Typography>
            </Field>

            <Field label="Sauf">
              <TextField name="exclude" value={ia.exclude || ""} onChange={(e) => setIA({ exclude: e.target.value })} placeholder="Ex: Casa, Tanger…" fullWidth sx={fieldSx} />
            </Field>

            <Field label="Compétences">
              <ChipInput value={ia.skills || []} onChange={(chips) => setIA({ ...ia, skills: chips })} fullWidth />
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontStyle: 'italic', mt: 0.5 }}>Choix multiples</Typography>
            </Field>

            <Field label="Langages de programmation">
              <ChipInput value={ia.programming_languages || []} onChange={(chips) => setIA({ ...ia, programming_languages: chips })} fullWidth variant="outlined" />
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontStyle: 'italic', mt: 0.5 }}>Choix multiples</Typography>
            </Field>

            <Field label="Rôle">
              <TextField name="role" value={ia.role || ""} onChange={(e) => setIA({ role: e.target.value })} fullWidth sx={fieldSx} />
            </Field>

            <Box sx={{ mb: 1.25 }}>
              <FieldLabel>Expérience (années)</FieldLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <TextField type="number" placeholder="Min" value={ia.experience?.min ?? ""} onChange={(e) => setExperienceField("min", e.target.value === "" ? undefined : Number(e.target.value))} fullWidth sx={fieldSx} />
                <Typography sx={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 600, flexShrink: 0 }}>—</Typography>
                <TextField type="number" placeholder="Max" value={ia.experience?.max ?? ""} onChange={(e) => setExperienceField("max", e.target.value === "" ? undefined : Number(e.target.value))} fullWidth sx={fieldSx} />
              </Box>
            </Box>

            <Field label="Domaine / Industrie">
              <ChipInput value={ia.industry || []} onChange={(chips) => setIA({ ...ia, industry: chips })} fullWidth variant="outlined" />
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontStyle: 'italic', mt: 0.5 }}>Choix multiples</Typography>
            </Field>

            <ApplyButton startIcon={<BiSearch size={15} />} disabled={iaEmpty} onClick={handleApplyIaSearch}>
              Appliquer (IA)
            </ApplyButton>
          </TabPanel>

          <TabPanel value={iaTab} index={1}>
            <Field label="Description du profil recherché">
              <TextField
                multiline minRows={6}
                placeholder="Ex : Développeur React senior, 5 ans d'expérience, basé à Casablanca, compétent en TypeScript et Node.js..."
                value={agentText} onChange={(e) => setAgentText(e.target.value)}
                fullWidth variant="outlined" sx={multilineSx}
              />
            </Field>
            <ApplyButton
              startIcon={agentLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <BiSearch size={15} />}
              disabled={!agentText.trim() || agentLoading} onClick={handleApplyAgentSearch}
            >
              {agentLoading ? "Recherche en cours…" : "Rechercher (Agent IA)"}
            </ApplyButton>
          </TabPanel>
        </TabPanel>
      </Box>
    </Box>
  );
};

export default MultiCritere;
